// Centralized Axios Configuration with Interceptors
import axios from 'axios';
import { notification } from 'antd';
import config from '../config';
import { STORAGE_KEYS } from '../utils/constants';
import { showSessionInvalidatedModal } from '../Components/common/SessionInvalidatedModal';
import { showSessionExpiredModal } from '../Components/common/SessionExpiredModal';

// Global flag: once an auth modal is showing, silence ALL further notification.error
// calls so no raw "SESSION_INVALIDATED" toast can leak from any component's .catch handler.
let authModalActive = false;
const markAuthModalActive = () => {
  authModalActive = true;
};

// Monkey-patch antd notification.error to drop calls while auth modal is active.
// Other methods (success, info, warning) still work.
const originalNotificationError = notification.error.bind(notification);
notification.error = (opts) => {
  if (authModalActive) return;
  return originalNotificationError(opts);
};

const getErrorCode = (error) =>
  error?.response?.data?.data?.code || error?.response?.data?.message || null;

// Routes that are part of the pre-login flow. We never auto-refresh or treat
// their 401s as "session expired" — the caller hasn't signed in yet, and
// refreshing would just chase a session that doesn't exist.
const isPreLoginRoute = (url = '') =>
  url.includes('/auth/login') ||
  url.includes('/auth/verify-otp') ||
  url.includes('/auth/resend-otp') ||
  url.includes('/auth/magic-link/') ||
  url.includes('/auth/verify-invite') ||
  url.includes('/auth/validate-session') ||
  url.includes('/auth/set-password') ||
  url.includes('/auth/refresh-token');

// ─────────────────────────── REFRESH-TOKEN COORDINATOR ───────────────────────────
// Concurrent 401s from N in-flight requests would otherwise trigger N parallel
// refresh attempts. We coalesce them: the first 401 starts the refresh; the
// rest await the same promise. On success, every queued request retries with
// the new access token. On failure, every queued request is rejected and the
// session-expired modal fires once.
let refreshInFlight = null;

const performRefresh = async () => {
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  if (!refreshToken) throw new Error('no-refresh-token');

  // Bare axios call — we MUST NOT go through `api` (would loop interceptors)
  // and we don't want our own Authorization header attached (the refresh
  // endpoint authenticates via the refresh token in the body).
  const resp = await axios.post(
    `${config.api.base_url}/auth/refresh-token`,
    { refreshToken },
    { timeout: 15000 }
  );

  const data = resp?.data?.data || {};
  const newAccess = data.accessToken;
  const newRefresh = data.refreshToken;
  if (!newAccess) throw new Error('refresh-response-missing-access-token');

  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, newAccess);
  // Backend rotates refresh tokens on every refresh — the OLD token is now
  // burned. Persisting the new one is mandatory; without it, the next refresh
  // hits reuse-detection and revokes ALL of this user's sessions.
  if (newRefresh) {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefresh);
  }
  return newAccess;
};

const refreshAccessToken = () => {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      // Reset whether success or failure so the next 401 can try again later
      // (e.g. after the user completes a new login).
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

// Returns true when the 401 is an auth failure we're handling via modal
// (so the response interceptor should SWALLOW the promise rejection
// — preventing every page's .catch() handler from firing its own raw error toast).
const handle401Modal = (error) => {
  // If there's no access token, user is already signed out. Don't re-trigger modal loops.
  const hasToken = !!localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  if (!hasToken) {
    return false;
  }

  // Clear any raw error toasts already on screen AND block any future ones for this session
  notification.destroy();
  markAuthModalActive();

  const code = getErrorCode(error);
  if (code === 'SESSION_INVALIDATED') {
    // "You signed in on another device" / refresh token reuse detected
    showSessionInvalidatedModal();
  } else {
    // Default: token expired / invalid → friendly modal + 5s countdown, auto-redirects
    showSessionExpiredModal();
  }
  return true;
};

const handle429 = (error) => {
  const data = error?.response?.data;
  const description =
    data?.message || 'Too many requests. Please wait a moment and try again.';
  const retryAfterRaw = error?.response?.headers?.['retry-after'];
  const retryAfter = Number(retryAfterRaw);
  const fullDescription = Number.isFinite(retryAfter) && retryAfter > 0
    ? `${description} Try again in ${retryAfter}s.`
    : description;

  notification.warning({
    message: 'Slow down',
    description: fullDescription,
    placement: 'topRight',
    duration: 5,
  });
};

// Create axios instance with base configuration
const api = axios.create({
  baseURL: config.api.base_url,
  timeout: 30000,
});

// Request interceptor - adds auth token to all requests
api.interceptors.request.use(
  (requestConfig) => {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (token) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }
    return requestConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handles 401 / 403 / 429 errors globally
const buildResponseInterceptor = (axiosInstance, retryViaInstance) => async (error) => {
  const status = error.response?.status;
  const originalRequest = error.config || {};
  const url = originalRequest.url || '';

  if (status === 429) {
    handle429(error);
    return Promise.reject(error);
  }

  if (status === 401) {
    // Pre-login routes handle their own errors and never have a session to refresh.
    if (isPreLoginRoute(url)) {
      return Promise.reject(error);
    }

    // Don't loop: if a request has already been retried once, surface the failure.
    const hasRefreshToken = !!localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (originalRequest._retried || !hasRefreshToken) {
      const handled = handle401Modal(error);
      if (handled) {
        return new Promise(() => {});
      }
      return Promise.reject(error);
    }

    // Treat refresh-only-fixable cases (token expired) as refresh candidates;
    // hard SESSION_INVALIDATED (server already revoked us) goes straight to modal.
    const code = getErrorCode(error);
    if (code === 'SESSION_INVALIDATED') {
      const handled = handle401Modal(error);
      if (handled) return new Promise(() => {});
      return Promise.reject(error);
    }

    try {
      const newAccess = await refreshAccessToken();
      originalRequest._retried = true;
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newAccess}`;
      return retryViaInstance(originalRequest);
    } catch (refreshErr) {
      const handled = handle401Modal(error);
      if (handled) return new Promise(() => {});
      return Promise.reject(error);
    }
  }

  if (status === 403) {
    const errorCode = error.response?.data?.data?.code;
    const isLoginError = ['ACCOUNT_PENDING', 'ACCOUNT_SUSPENDED'].includes(errorCode);
    // A caller that renders its own inline forbidden state opts out of the
    // global toast, otherwise the user is told twice.
    const suppressToast = originalRequest.suppressForbiddenNotification === true;
    if (!isLoginError && !suppressToast) {
      notification.error({
        message: 'Access Denied',
        description: error.response?.data?.message || 'You do not have permission to perform this action.',
        placement: 'topRight',
        duration: 4,
      });
    }
    return Promise.reject(error);
  }

  return Promise.reject(error);
};

api.interceptors.response.use(
  (response) => response,
  buildResponseInterceptor(api, (cfg) => api(cfg))
);

// Backward-compat: also handle errors on the default axios instance for any
// legacy callers. Uses the same refresh + 429 logic.
axios.interceptors.response.use(
  (response) => response,
  buildResponseInterceptor(axios, (cfg) => axios(cfg))
);

export default api;
